/**
 * Team performance — a vertical and everyone inside it.
 *
 * The previous version of this screen was a VP league table, which answers
 * "how big is this vertical" and nothing else. The question a VP actually
 * arrives with is "who in my team is carrying this and who has gone quiet",
 * and that is a different set of people every quarter.
 *
 * So a vertical opens into its managers and its employees, all three tiers
 * computed from the same facts. The concentration figure is the one to read
 * first: a vertical where three people produce most of the revenue is a
 * different management problem from one where thirty do, and the totals look
 * identical.
 */

import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, ArrowLeft, Users2 } from "lucide-react";

import { api } from "@/api/client";
import { Disclosure } from "@/components/cards/Disclosure";
import { KpiCard } from "@/components/cards/KpiCard";
import { DataTable, type Column } from "@/components/tables/DataTable";
import { useFilterStore } from "@/store/filters";
import type { Metadata, Quality } from "@/types/api";
import { count, inr, pct } from "@/utils/format";

interface TeamRow {
  key: string;
  name: string;
  designation: string;
  branch: string;
  rank: number;
  activities: number;
  information: number;
  positive_responses: number;
  clients: number;
  admissions: number;
  conversion_pct: number | null;
  revenue: number;
  commission: number;
  credits: number;
  payout_ratio_pct: number | null;
  headcount: number;
  revenue_per_head: number | null;
  // Team membership resolved as of the period, not as of today.
  days_in_team?: number;
  period_days?: number;
  tenure_pct?: number | null;
  full_period?: boolean;
  attribution_mismatch?: boolean;
  joined_team?: string | null;
  left_team?: string | null;
}

interface TeamPayload {
  summary: Record<string, unknown> & {
    verticals?: number; employees?: number; revenue?: number;
    name?: string; managers?: number;
    employees_with_no_revenue?: number;
    part_period_members?: number;
    not_in_team_this_period?: number;
    period_days?: number;
    employee_revenue_share_top3?: number | null;
    conversion_pct?: number | null;
  };
  breakdown: TeamRow[];
  trend: TeamRow[];
  quality: Quality;
  metadata: Metadata;
}

export function TeamPerformance() {
  const navigate = useNavigate();
  const params = useFilterStore((s) => s.queryParams());
  const [vp, setVp] = useState("");

  const { data, isLoading } = useQuery({
    queryKey: ["teams", params, vp],
    queryFn: async () => {
      const { data } = await api.get<TeamPayload>("/analytics/teams", {
        params: { ...params, vp: vp || undefined },
      });
      return data;
    },
    staleTime: 120_000,
  });

  const summary = data?.summary;
  const rows = data?.breakdown ?? [];
  const managers = data?.trend ?? [];

  const shared = useMemo<Column<TeamRow>[]>(() => [
    {
      key: "revenue", header: "Revenue", align: "right",
      render: (r) => inr(r.revenue), value: (r) => r.revenue,
    },
    {
      key: "admissions", header: "Admissions", align: "right",
      value: (r) => r.admissions,
    },
    {
      key: "information", header: "Information", align: "right",
      render: (r) => (
        <span title="`New Leads` activities — patient details handed over">
          {count(r.information)}
        </span>
      ),
      value: (r) => r.information,
    },
    {
      key: "conversion_pct", header: "Conversion", align: "right",
      render: (r) => pct(r.conversion_pct), value: (r) => r.conversion_pct,
    },
    {
      key: "activities", header: "Activities", align: "right",
      value: (r) => r.activities,
    },
    {
      key: "clients", header: "Clients", align: "right", value: (r) => r.clients,
    },
    {
      key: "commission", header: "Commission", align: "right", hidden: true,
      render: (r) => inr(r.commission), value: (r) => r.commission,
    },
    {
      key: "payout_ratio_pct", header: "Payout", align: "right", hidden: true,
      render: (r) => pct(r.payout_ratio_pct), value: (r) => r.payout_ratio_pct,
    },
  ], []);

  const verticalColumns = useMemo<Column<TeamRow>[]>(() => [
    {
      key: "name", header: "Vertical", width: "210px",
      render: (r) => (
        <span className="min-w-0">
          <span className="font-medium truncate block">{r.name}</span>
          <span className="text-[11px] text-ink-faint">
            {r.headcount} in team
          </span>
        </span>
      ),
    },
    ...shared,
    {
      key: "revenue_per_head", header: "Per person", align: "right",
      render: (r) => inr(r.revenue_per_head), value: (r) => r.revenue_per_head,
    },
  ], [shared]);

  const personColumns = useMemo<Column<TeamRow>[]>(() => [
    {
      key: "name", header: "Person", width: "230px",
      render: (r) => (
        <span className="min-w-0">
          <span className="font-medium truncate block">{r.name}</span>
          <span className="text-[11px] text-ink-faint truncate block">
            {r.designation || "designation not set"}
            {r.branch ? ` · ${r.branch}` : ""}
          </span>
        </span>
      ),
    },
    {
      // The column that makes a promotion legible. A row at 0 days is not a
      // team member whose numbers happen to be small — it is attribution
      // pointing at a vertical the person had already left.
      key: "days_in_team", header: "In team", align: "right", width: "132px",
      value: (r) => r.tenure_pct ?? 0,
      render: (r) => {
        if (r.days_in_team === undefined) return <span className="text-ink-faint">—</span>;
        if (r.attribution_mismatch) {
          return (
            <span className="inline-flex items-center gap-1.5 justify-end w-full
                             text-warn text-[11.5px]"
                  title="Facts are stamped to this vertical, but no reporting-line record puts this person in it during the selected period. A stale line in the source — their earlier work still shows correctly under the period they were here.">
              <AlertTriangle size={11} className="shrink-0" />
              not in team
            </span>
          );
        }
        if (r.full_period) {
          return <span className="text-ink-faint text-[11.5px]">whole period</span>;
        }
        return (
          <span className="inline-flex items-center gap-1.5 justify-end w-full"
                title={[
                  r.joined_team ? `joined ${r.joined_team}` : null,
                  r.left_team ? `left ${r.left_team}` : null,
                ].filter(Boolean).join(" · ") || undefined}>
            <span className="font-mono tnum text-[12px]">
              {r.days_in_team}/{r.period_days}d
            </span>
            <span className="w-10 h-1.5 rounded-full bg-surface-alt overflow-hidden shrink-0">
              <span className="block h-full rounded-full bg-warn"
                    style={{ width: `${r.tenure_pct ?? 0}%` }} />
            </span>
          </span>
        );
      },
    },
    ...shared,
  ], [shared]);

  // ---- vertical list -------------------------------------------------
  if (!vp) {
    return (
      <div className="space-y-5">
        <div>
          <h1 className="font-display font-bold text-[22px] sm:text-[24px] tracking-tight">
            Team performance
          </h1>
          <p className="text-[13.5px] text-ink-muted mt-0.5 max-w-3xl">
            Each vertical, and inside it the managers and employees who make it
            up. Open one to see who is carrying it.
          </p>
        </div>

        <div className="grid gap-3"
             style={{ gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 190px), 1fr))" }}>
          <KpiCard loading={isLoading} label="Verticals"
                   value={count(summary?.verticals)} />
          <KpiCard loading={isLoading} label="People active"
                   value={count(summary?.employees)} />
          <KpiCard loading={isLoading} label="Revenue"
                   value={inr(summary?.revenue)} />
        </div>

        <DataTable<TeamRow>
          rows={rows}
          columns={verticalColumns}
          rowKey={(r) => r.key}
          onRowClick={(r) => setVp(r.key)}
          exportName="verticals"
          loading={isLoading}
          emptyMessage="No verticals with activity in this period."
          footnote="Click a vertical to open its managers and employees. VP and Manager are the same person on 50.2% of hierarchy rows, so a vertical's total is not the sum of its managers — each tier is grouped on its own column."
        />

        <Disclosure quality={data?.quality} metadata={data?.metadata} />
      </div>
    );
  }

  // ---- one vertical, opened -------------------------------------------
  return (
    <div className="space-y-5">
      <button onClick={() => setVp("")}
              className="inline-flex items-center gap-1.5 text-[12.5px] text-petrol
                         hover:underline">
        <ArrowLeft size={13} /> All verticals
      </button>

      <div>
        <div className="label">vertical</div>
        <h1 className="font-display font-bold text-[22px] sm:text-[24px] tracking-tight">
          {String(summary?.name ?? vp)}
        </h1>
        <p className="text-[13.5px] text-ink-muted mt-0.5">
          {count(summary?.managers)} managers · {count(summary?.employees)} employees
        </p>
      </div>

      <div className="grid gap-3"
           style={{ gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 190px), 1fr))" }}>
        <KpiCard loading={isLoading} label="Revenue"
                 value={inr(summary?.revenue as number)} />
        <KpiCard loading={isLoading} label="Conversion"
                 value={pct(summary?.conversion_pct)}
                 footnote="admissions ÷ information received" />
        <KpiCard loading={isLoading} label="Carried by top 3"
                 value={summary?.employee_revenue_share_top3 == null
                   ? "—" : `${summary.employee_revenue_share_top3}%`}
                 higherIsBetter={false}
                 footnote="share of the vertical's revenue" />
        <KpiCard loading={isLoading} label="No revenue this period"
                 value={count(summary?.employees_with_no_revenue)}
                 higherIsBetter={false} />
      </div>

      {(Number(summary?.not_in_team_this_period ?? 0) > 0
        || Number(summary?.part_period_members ?? 0) > 0) && (
        <div className="card p-4 flex items-start gap-2">
          <AlertTriangle size={14} className="mt-0.5 shrink-0 text-warn" />
          <p className="text-[13px] text-ink-soft">
            Membership is resolved <strong>as of this period</strong>, not as of
            today.
            {Number(summary?.part_period_members ?? 0) > 0 && (
              <> {count(summary?.part_period_members)} people were here for only
              part of it — their totals are not comparable with a full-period
              peer's.</>
            )}
            {Number(summary?.not_in_team_this_period ?? 0) > 0 && (
              <> {count(summary?.not_in_team_this_period)} have work stamped to
              this vertical but no reporting-line record placing them in it —
              a stale line in the source. Their earlier work still appears
              correctly under the periods they were actually here.</>
            )}
          </p>
        </div>
      )}

      {managers.length > 0 && (
        <section className="space-y-2">
          <h2 className="font-display font-semibold text-[16px] flex items-center gap-2">
            <Users2 size={15} className="text-ink-faint" /> Managers
          </h2>
          <DataTable<TeamRow>
            rows={managers}
            columns={personColumns.map((c) => (c.key === "name"
              ? { ...c, header: "Manager" } : c))}
            rowKey={(r) => r.key}
            onRowClick={(r) => navigate(`/employees/${encodeURIComponent(r.key)}`)}
            exportName="managers"
            searchable={false}
            loading={isLoading}
            emptyMessage="No managers recorded under this vertical."
          />
        </section>
      )}

      <section className="space-y-2">
        <h2 className="font-display font-semibold text-[16px]">Employees</h2>
        <DataTable<TeamRow>
          rows={rows}
          columns={personColumns}
          rowKey={(r) => r.key}
          onRowClick={(r) => navigate(`/employees/${encodeURIComponent(r.key)}`)}
          exportName="team-employees"
          loading={isLoading}
          emptyMessage="No employees recorded under this vertical."
          footnote="Click a row for that person's profile and journey timeline."
        />
      </section>

      <Disclosure quality={data?.quality} metadata={data?.metadata} />
    </div>
  );
}
